import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Table,
  Space,
  Tag,
  Input,
  message,
  Modal,
  Form,
  Select,
  Switch,
  Drawer,
  Tabs,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";
import CreateClinicModal from "./CreateClinicModal";

const { Option } = Select;

export default function ClinicsMenu() {
  const { t } = useTranslation(["App", "Backend"]);
  const { token } = useToken();

  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [createOpen, setCreateOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);

  // ✅ Drawer: professionnels associés à une clinique
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clinicForUsers, setClinicForUsers] = useState(null);
  const [activeTab, setActiveTab] = useState("doctors");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [clinicKins, setClinicKins] = useState([]);
  const [clinicManagers, setClinicManagers] = useState([]);

  const fetchClinics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${Constants.SERVER_URL}/clinics`, {
        headers: { Authorization: "Bearer " + token },
      });
      setClinics(res.data || []);
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          t("clinic_error_loading_list", "Erreur lors du chargement des cliniques")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchClinics();
    // eslint-disable-next-line
  }, [token]);

  const filteredClinics = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return clinics;

    return clinics.filter((c) => {
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.type || "").toLowerCase().includes(s) ||
        (c.city || "").toLowerCase().includes(s) ||
        (c.postalCode || "").toLowerCase().includes(s)
      );
    });
  }, [clinics, searchTerm]);

  const disableClinic = async (id) => {
    await axios.patch(
      `${Constants.SERVER_URL}/clinics/${id}/disable`,
      {},
      { headers: { Authorization: "Bearer " + token } }
    );
  };

  const enableClinic = async (clinic) => {
    await axios.put(
      `${Constants.SERVER_URL}/clinics/${clinic.id}`,
      { ...clinic, isActive: true },
      { headers: { Authorization: "Bearer " + token } }
    );
  };

  const toggleActive = async (clinic) => {
    try {
      if (clinic.isActive) {
        await disableClinic(clinic.id);
        message.success(t("clinic_success_disabled", "Clinique désactivée"));
      } else {
        await enableClinic(clinic);
        message.success(t("clinic_success_enabled", "Clinique activée"));
      }
      fetchClinics();
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          t("clinic_error_updating_status", "Erreur lors de la mise à jour du statut")
      );
    }
  };

  // Fetch doctors + kins + managers for a clinic
  const fetchProfessionalsByClinic = async (clinicId) => {
    try {
      setLoadingUsers(true);

      const [docsRes, kinsRes, managersRes] = await Promise.all([
        axios.get(`${Constants.SERVER_URL}/clinics/clinics/${clinicId}/doctors`, {
          headers: { Authorization: "Bearer " + token },
        }),
        axios.get(`${Constants.SERVER_URL}/clinics/clinics/${clinicId}/kinesiologists`, {
          headers: { Authorization: "Bearer " + token },
        }),
        axios.get(`${Constants.SERVER_URL}/clinics/clinics/${clinicId}/managers`, {
          headers: { Authorization: "Bearer " + token },
        }),
      ]);

      setClinicDoctors(docsRes.data || []);
      setClinicKins(kinsRes.data || []);
      setClinicManagers(managersRes.data || []);
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          t(
            "clinic_error_loading_professionals",
            "Erreur lors du chargement des professionnels"
          )
      );
      setClinicDoctors([]);
      setClinicKins([]);
      setClinicManagers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const openProfessionalsDrawer = async (clinic) => {
    setClinicForUsers(clinic);
    setDrawerOpen(true);
    setActiveTab("doctors");
    await fetchProfessionalsByClinic(clinic.id);
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    { title: t("clinic_col_name", "Nom"), dataIndex: "name", key: "name" },
    { title: t("clinic_col_type", "Type"), dataIndex: "type", key: "type", width: 130 },
    { title: t("clinic_col_city", "Ville"), dataIndex: "city", key: "city", width: 140 },
    {
      title: t("clinic_col_postal_code", "Code postal"),
      dataIndex: "postalCode",
      key: "postalCode",
      width: 120,
    },
    { title: t("clinic_col_phone", "Téléphone"), dataIndex: "phone", key: "phone", width: 150 },
    {
      title: t("clinic_col_status", "Statut"),
      key: "status",
      width: 110,
      render: (_, c) =>
        c.isActive ? (
          <Tag color="green">{t("clinic_status_active", "Active")}</Tag>
        ) : (
          <Tag color="red">{t("clinic_status_inactive", "Inactive")}</Tag>
        ),
    },
    {
      title: t("clinic_col_actions", "Actions"),
      key: "actions",
      width: 360,
      render: (_, c) => (
        <Space>
          <Button
            icon={<TeamOutlined />}
            onClick={() => openProfessionalsDrawer(c)}
          >
            {t("clinic_action_view_professionals", "Voir pros")}
          </Button>

          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedClinic(c);
              setEditOpen(true);
            }}
          >
            {t("clinic_action_edit", "Modifier")}
          </Button>

          <Button
            danger={c.isActive}
            icon={c.isActive ? <StopOutlined /> : <CheckOutlined />}
            onClick={() => toggleActive(c)}
          >
            {c.isActive
              ? t("clinic_action_disable", "Désactiver")
              : t("clinic_action_enable", "Activer")}
          </Button>
        </Space>
      ),
    },
  ];

  // Tables internes (dans Drawer)
  const doctorCols = [
    { title: "ID", dataIndex: "idDoctor", key: "idDoctor", width: 90 },
    {
      title: t("clinic_col_name", "Nom"),
      key: "name",
      render: (_, row) => `${row?.user?.firstname || ""} ${row?.user?.lastname || ""}`.trim(),
    },
    { title: "Email", key: "email", render: (_, row) => row?.user?.email || "" },
    { title: t("clinic_col_phone", "Téléphone"), key: "phone", render: (_, row) => row?.user?.phoneNumber || "" },
    {
      title: t("clinic_col_status", "Statut"),
      key: "active",
      width: 110,
      render: (_, row) =>
        row?.user?.active ? (
          <Tag color="green">{t("clinic_status_user_active", "Actif")}</Tag>
        ) : (
          <Tag color="red">{t("clinic_status_user_inactive", "Inactif")}</Tag>
        ),
    },
  ];

  const kinCols = [
    { title: "ID", dataIndex: "idKinesiologist", key: "idKinesiologist", width: 90 },
    {
      title: t("clinic_col_name", "Nom"),
      key: "name",
      render: (_, row) => `${row?.user?.firstname || ""} ${row?.user?.lastname || ""}`.trim(),
    },
    { title: "Email", key: "email", render: (_, row) => row?.user?.email || "" },
    { title: t("clinic_col_phone", "Téléphone"), key: "phone", render: (_, row) => row?.user?.phoneNumber || "" },
    {
      title: t("clinic_col_status", "Statut"),
      key: "active",
      width: 110,
      render: (_, row) =>
        row?.user?.active ? (
          <Tag color="green">{t("clinic_status_user_active", "Actif")}</Tag>
        ) : (
          <Tag color="red">{t("clinic_status_user_inactive", "Inactif")}</Tag>
        ),
    },
  ];

  const managerCols = [
    { title: "ID", dataIndex: "idClinicManager", key: "idClinicManager", width: 90 },
    {
      title: t("clinic_col_name", "Nom"),
      key: "name",
      render: (_, row) => `${row?.user?.firstname || ""} ${row?.user?.lastname || ""}`.trim(),
    },
    { title: "Email", key: "email", render: (_, row) => row?.user?.email || "" },
    { title: t("clinic_col_phone", "Téléphone"), key: "phone", render: (_, row) => row?.user?.phoneNumber || "" },
    {
      title: t("clinic_col_status", "Statut"),
      key: "active",
      width: 110,
      render: (_, row) =>
        row?.user?.active ? (
          <Tag color="green">{t("clinic_status_user_active", "Actif")}</Tag>
        ) : (
          <Tag color="red">{t("clinic_status_user_inactive", "Inactif")}</Tag>
        ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Input
          placeholder={t("clinic_search_placeholder", "Rechercher (nom, ville, code postal...)")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 340 }}
        />

        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          {t("clinic_add_button", "Ajouter une clinique")}
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredClinics}
        columns={columns}
        pagination={{ pageSize: 8 }}
      />

      <CreateClinicModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => fetchClinics()}
      />

      <EditClinicModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        clinic={selectedClinic}
        onUpdated={() => fetchClinics()}
      />

      {/* ✅ Drawer Pros */}
      <Drawer
        title={
          clinicForUsers
            ? `${t("clinic_drawer_title_prefix", "Professionnels")} — ${clinicForUsers.name} (${
                clinicForUsers.type || "AUTRE"
              })`
            : t("clinic_drawer_title_prefix", "Professionnels")
        }
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setClinicForUsers(null);
          setClinicDoctors([]);
          setClinicKins([]);
          setClinicManagers([]);
        }}
        width={900}
        destroyOnClose
      >
        <Spin spinning={loadingUsers}>
          <Tabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k)}
            items={[
              {
                key: "doctors",
                label: `${t("clinic_tab_doctors", "Médecins")} (${clinicDoctors.length})`,
                children: (
                  <Table
                    rowKey={(r) => `doc-${r.idDoctor}`}
                    dataSource={clinicDoctors}
                    columns={doctorCols}
                    pagination={{ pageSize: 6 }}
                  />
                ),
              },
              {
                key: "kinesiologists",
                label: `${t("clinic_tab_kinesiologists", "Kinésiologues")} (${clinicKins.length})`,
                children: (
                  <Table
                    rowKey={(r) => `kin-${r.idKinesiologist}`}
                    dataSource={clinicKins}
                    columns={kinCols}
                    pagination={{ pageSize: 6 }}
                  />
                ),
              },
              {
                key: "managers",
                label: `${t("clinic_tab_managers", "Gestionnaires")} (${clinicManagers.length})`,
                children: (
                  <Table
                    rowKey={(r) => `mgr-${r.idClinicManager}`}
                    dataSource={clinicManagers}
                    columns={managerCols}
                    pagination={{ pageSize: 6 }}
                  />
                ),
              },
            ]}
          />
        </Spin>
      </Drawer>
    </div>
  );
}

/** Modal édition */
function EditClinicModal({ open, onClose, clinic, onUpdated }) {
  const { t } = useTranslation(["App", "Backend"]);
  const { token } = useToken();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && clinic) {
      form.setFieldsValue({
        name: clinic.name,
        type: clinic.type,
        postalCode: clinic.postalCode,
        addressLine: clinic.addressLine,
        city: clinic.city,
        province: clinic.province,
        phone: clinic.phone,
        isActive: clinic.isActive,
      });
    }
  }, [open, clinic, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      await axios.put(`${Constants.SERVER_URL}/clinics/${clinic.id}`, values, {
        headers: { Authorization: "Bearer " + token },
      });

      message.success(t("clinic_success_updated", "Clinique modifiée"));
      onUpdated?.();
      onClose();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("clinic_edit_modal_title", "Modifier la clinique")}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={t("clinic_edit_modal_save", "Enregistrer")}
      cancelText={t("clinic_edit_modal_cancel", "Annuler")}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("clinic_form_name", "Nom")}
          name="name"
          rules={[{ required: true, message: t("clinic_form_name_required", "Le nom est obligatoire") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("clinic_form_type", "Type")}
          name="type"
          rules={[{ required: true, message: t("clinic_form_type_required", "Le type est obligatoire") }]}
        >
          <Select placeholder={t("clinic_form_type_placeholder", "Choisir un type")}>
            <Option value="CHSLD">CHSLD</Option>
            <Option value="RPA">RPA</Option>
            <Option value="GMF">GMF</Option>
            <Option value="HOPITAL">{t("clinic_type_hospital", "Hôpital")}</Option>
            <Option value="CLINIQUE">{t("clinic_type_clinic", "Clinique")}</Option>
            <Option value="AUTRE">{t("clinic_type_other", "Autre")}</Option>
          </Select>
        </Form.Item>

        <Form.Item label={t("clinic_form_postal_code", "Code postal")} name="postalCode">
          <Input />
        </Form.Item>

        <Form.Item label={t("clinic_form_address", "Adresse")} name="addressLine">
          <Input />
        </Form.Item>

        <Form.Item label={t("clinic_form_city", "Ville")} name="city">
          <Input />
        </Form.Item>

        <Form.Item label={t("clinic_form_province", "Province")} name="province">
          <Input />
        </Form.Item>

        <Form.Item label={t("clinic_form_phone", "Téléphone")} name="phone">
          <Input />
        </Form.Item>

        <Form.Item label={t("clinic_form_active", "Active")} name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
